import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { api, type PublicUser, type Role, type ServerSummary } from "@shared/api";
import { Mark, Tile, fromPublic, markFor, type Person } from "@entities/user";
import { assignableRoles } from "@features/moderate-member";
import { plural } from "@shared/lib/format";
import { bannerTint } from "@shared/lib/tint";
import { Hint } from "@shared/ui/bits";
import { FileImage } from "@shared/ui/FileImage";
import { EmojiPick, insertInto } from "@shared/ui/EmojiPick";
import { Menu, SEPARATOR, withId, type MenuItem } from "@shared/ui/Menu";
import {
  IconClose,
  IconFriend,
  IconFriendPending,
  IconInvite,
  IconMore,
  IconPlus,
  IconSafety,
} from "@shared/ui/icons";
import { dmProblem, type ProfileServer } from "../lib/profile";

/** Имя на сервере — ник поверх общего имени, как в Discord. */
export function nameIn(user: PublicUser, server?: ProfileServer | null): string {
  return server?.member?.nickname || user.display_name || user.username;
}

/** Точка у края кнопки — откуда открыть меню. */
const below = (element: HTMLElement) => {
  const box = element.getBoundingClientRect();
  return { x: box.left, y: box.bottom + 6 };
};

/**
 * Меню из карточки — порталом в корень. Опора меню стоит `fixed` в точке
 * щелчка, а у карточки и окна есть `transform`: внутри них `fixed` считается
 * от них, и меню уезжало за край экрана. Для Radix меню остаётся внутри
 * карточки — по дереву React, — и щелчок в нём карточку не закрывает.
 */
function MenuAt(props: Parameters<typeof Menu>[0]) {
  return createPortal(<Menu {...props} />, document.body);
}

/**
 * Баннер — то, что выбрано в профиле, как в редакторе: картинка, без неё цвет
 * баннера (он же подложка, пока картинка грузится), без обоих — градиент
 * тоном по хэндлу. Под аватаром в баннере вырез: сквозь него видно материал
 * карточки, и кольцо вокруг аватара совпадает с фоном при любом размытии под
 * окном. Где вырез — говорят переменные карточки (`--av-*`).
 */
export function Banner({ user, className = "" }: { user: PublicUser; className?: string }) {
  return (
    <div
      className={`profile-head-banner ${user.banner_id ? "image" : ""} ${className}`}
      style={{ background: user.banner_color ?? bannerTint(user.handle) }}
    >
      {user.banner_id && <FileImage id={user.banner_id} alt="" />}
    </div>
  );
}

/**
 * Аватар с меткой присутствия. Метка лежит в вырезе аватара, а не на подложке
 * своего цвета: под ней материал карточки, как под кольцом.
 */
export function Avatar({
  user,
  person,
  size,
  hidden,
  onOpen,
}: {
  user: PublicUser;
  person: Person;
  size: number;
  /** Нас заблокировали — присутствия нет, метку не показываем. */
  hidden: boolean;
  /** Щелчок по аватару — полный профиль, как в Discord. */
  onOpen?: () => void;
}) {
  const kind = hidden ? null : markFor(person);
  const body = (
    <>
      <Tile person={person} size={size} mark={null} />
      {kind && (
        <span className="profile-av-mark">
          <Mark kind={kind} size={Math.round(size * 0.2)} />
        </span>
      )}
    </>
  );
  const marked = kind ? "marked" : "";
  return onOpen ? (
    <button type="button" className={`profile-av ${marked}`} onClick={onOpen} aria-label={`Профиль ${user.username}`}>
      {body}
    </button>
  ) : (
    <span className={`profile-av ${marked}`}>{body}</span>
  );
}

/**
 * Значки рядом с хэндлом — как в Discord. Свой значок пока один: команда
 * Airel (`privileged`). Биты `badges` на сервере ещё не договорены, их не
 * рисуем.
 */
export function Badges({ user }: { user: PublicUser }) {
  if (!user.privileged) return null;
  return (
    <span className="profile-badges">
      <Hint label="Команда Airel">
        <span className="profile-badge" aria-label="Команда Airel">
          <IconSafety size={14} />
        </span>
      </Hint>
    </span>
  );
}

/** Своя подпись и знак статуса — пузырём под именем. */
export function Status({ user, hidden }: { user: PublicUser; hidden: boolean }) {
  if (hidden || !(user.status_text || user.status_emoji)) return null;
  return (
    <div className="profile-status">
      {user.status_emoji && <span className="status-mark">{user.status_emoji}</span>}
      {user.status_text}
    </div>
  );
}

/**
 * «2 общих друга • 1 общий сервер» — как в Discord, с лицами друзей впереди.
 * Каждая половина ведёт на свою вкладку полного профиля.
 */
export function Shared({
  friends,
  servers,
  onFriends,
  onServers,
}: {
  friends: PublicUser[];
  servers: ServerSummary[];
  onFriends?: () => void;
  onServers?: () => void;
}) {
  if (!friends.length && !servers.length) return null;
  return (
    <div className="profile-shared">
      {friends.length > 0 && (
        <button type="button" className="profile-shared-part" onClick={onFriends} disabled={!onFriends}>
          <span className="profile-shared-faces" aria-hidden>
            {friends.slice(0, 3).map((friend) => (
              <Tile key={friend.id} person={fromPublic(friend)} size={16} mark={null} />
            ))}
          </span>
          {friends.length} {plural(friends.length, "общий друг", "общих друга", "общих друзей")}
        </button>
      )}
      {friends.length > 0 && servers.length > 0 && <span className="profile-shared-dot" aria-hidden />}
      {servers.length > 0 && (
        <button type="button" className="profile-shared-part" onClick={onServers} disabled={!onServers}>
          {servers.length} {plural(servers.length, "общий сервер", "общих сервера", "общих серверов")}
        </button>
      )}
    </div>
  );
}

/**
 * Роли человека на сервере — как в Discord: капсулы с точкой цвета, у тех,
 * что я вправе снять, крестик, в конце «+» со списком ещё не выданных.
 * Порядок — от старшей.
 */
export function Roles({ server, title }: { server: ProfileServer; title?: ReactNode }) {
  const [adding, setAdding] = useState<{ x: number; y: number } | null>(null);
  const member = server.member;
  if (!member) return null;
  const mine = [...server.roles]
    .filter((role) => member.roles.includes(role.id))
    .sort((a, b) => b.rank - a.rank);
  const assignable = assignableRoles(server.roles, server.rights);
  const canTouch = (role: Role) => assignable.some((item) => item.id === role.id);
  const missing = assignable.filter((role) => !member.roles.includes(role.id));
  if (!mine.length && !missing.length) return null;

  return (
    <div className="profile-section">
      {title}
      <div className="profile-roles">
        {mine.map((role) => (
          <span key={role.id} className="profile-role">
            <span className="role-dot" style={{ background: role.color ?? "var(--ui-text-dim)" }} />
            {role.icon && <span aria-hidden>{role.icon}</span>}
            <span className="ellipsis">{role.name}</span>
            {canTouch(role) && (
              <button
                type="button"
                className="profile-role-x"
                aria-label={`Снять роль ${role.name}`}
                onClick={() => server.onRole(role.id, false)}
              >
                <IconClose size={12} />
              </button>
            )}
          </span>
        ))}
        {missing.length > 0 && (
          <Hint label="Добавить роль">
            <button
              type="button"
              className="profile-role profile-role-add"
              aria-label="Добавить роль"
              onClick={(event) => setAdding(below(event.currentTarget))}
            >
              <IconPlus size={14} />
            </button>
          </Hint>
        )}
      </div>
      {adding && (
        <MenuAt
          at={adding}
          items={missing.map((role) => ({ label: role.name, onPick: () => server.onRole(role.id, true) }))}
          onClose={() => setAdding(null)}
        />
      )}
    </div>
  );
}

/** Круглая кнопка-значок с подсказкой — в углу карточки и в ряду окна. */
export function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Hint label={label}>
      <button type="button" className="profile-icon-btn" aria-label={label} disabled={disabled} onClick={onClick}>
        {children}
      </button>
    </Hint>
  );
}

/**
 * Дружба одной кнопкой — как в Discord: не друзья — «Добавить в друзья»,
 * позвали нас — «Принять», друзья и ушедшая заявка — меню с отменой.
 */
export function FriendButton({
  user,
  busy,
  act,
}: {
  user: PublicUser;
  busy: boolean;
  act: (action: () => Promise<unknown>) => void;
}) {
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);
  const relation = user.relationship;
  if (relation === "myself" || relation === "blocked" || relation === "blocked_other") return null;

  const add = () => act(() => api.relationships.add(user.id));
  const remove = () => act(() => api.relationships.remove(user.id));
  const button =
    relation === "friend" ? (
      <IconButton
        label="Друзья"
        disabled={busy}
        onClick={(event) =>
          setMenu({ ...below(event.currentTarget), items: [{ label: "Удалить из друзей", kind: "danger", onPick: remove }] })
        }
      >
        <IconFriend size={16} />
      </IconButton>
    ) : relation === "outgoing" ? (
      <IconButton
        label="Заявка отправлена"
        disabled={busy}
        onClick={(event) =>
          setMenu({ ...below(event.currentTarget), items: [{ label: "Отменить заявку в друзья", onPick: remove }] })
        }
      >
        <IconFriendPending size={16} />
      </IconButton>
    ) : (
      <IconButton label={relation === "incoming" ? "Принять заявку в друзья" : "Добавить в друзья"} disabled={busy} onClick={add}>
        <IconInvite size={16} />
      </IconButton>
    );

  return (
    <>
      {button}
      {menu && <MenuAt at={menu} items={menu.items} onClose={() => setMenu(null)} />}
    </>
  );
}

/**
 * «…» — как в Discord: полный профиль (из карточки), блок и хэндл. Дружба —
 * своей кнопкой рядом, модерация — в меню участника по правой кнопке.
 */
export function MoreButton({
  user,
  act,
  onOpenFull,
}: {
  user: PublicUser;
  act: (action: () => Promise<unknown>) => void;
  onOpenFull?: () => void;
}) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const items: MenuItem[] = [];
  if (onOpenFull) items.push({ label: "Открыть полный профиль", onPick: onOpenFull });
  if (user.relationship !== "myself") {
    if (items.length) items.push(SEPARATOR);
    items.push(
      user.relationship === "blocked"
        ? { label: "Разблокировать", onPick: () => act(() => api.relationships.unblock(user.id)) }
        : { label: "Заблокировать", kind: "danger", onPick: () => act(() => api.relationships.block(user.id)) },
    );
  }
  items.push(SEPARATOR, {
    label: "Копировать хэндл",
    hint: user.handle,
    onPick: () => void navigator.clipboard?.writeText(user.handle).catch(() => {}),
  });

  return (
    <>
      <IconButton label="Ещё" onClick={(event) => setAt(below(event.currentTarget))}>
        <IconMore size={16} />
      </IconButton>
      {at && <MenuAt at={at} items={withId(items, user.id)} onClose={() => setAt(null)} />}
    </>
  );
}

/**
 * Заметка — своя строка о человеке, видна только себе. Пишется прямо здесь и
 * уходит, когда поле теряет фокус.
 */
export function Note({
  note,
  onSave,
  rows = 1,
}: {
  note: string | null | undefined;
  onSave: (text: string) => Promise<void>;
  rows?: number;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <textarea
      className="profile-note-field"
      rows={rows}
      maxLength={256}
      placeholder="Нажмите, чтобы добавить заметку"
      aria-label="Заметка (видна только вам)"
      value={draft ?? note ?? ""}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={(event) => {
        // Черновик снимается на любом пути: иначе недописанное осталось бы висеть.
        const text = event.target.value;
        setDraft(null);
        void onSave(text);
      }}
    />
  );
}

/**
 * «Сообщение для @имя» внизу карточки — как в Discord: Enter отправляет в
 * личку и открывает её. Не открылась (личка только для друзей) — причина
 * строкой под полем, набранное остаётся.
 */
export function SendBox({
  user,
  onSend,
}: {
  user: PublicUser;
  onSend: (user: PublicUser, text: string) => Promise<void>;
}) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [field, setField] = useState<HTMLInputElement | null>(null);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setProblem(null);
    try {
      await onSend(user, body);
      setText("");
    } catch (error) {
      setProblem(dmProblem(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="profile-send-wrap">
      <div className="profile-send">
        <input
          ref={setField}
          value={text}
          maxLength={4000}
          disabled={sending}
          placeholder={`Сообщение для @${user.username}`}
          aria-label={`Сообщение для @${user.username}`}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              void send();
            }
          }}
        />
        <EmojiPick
          title="Знак"
          onPick={(value) => {
            const { next, caret } = insertInto(field, text, value);
            setText(next);
            requestAnimationFrame(() => {
              field?.focus();
              field?.setSelectionRange(caret, caret);
            });
          }}
        />
      </div>
      {problem && <div className="small danger-text">{problem}</div>}
    </div>
  );
}
